/**
 * NetworkContext
 * Provides centralized network state management via React Context
 */

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useNetworkState } from '@/hooks/useNetworkState';
import type { Node, Connection, NodeType } from '@/types';

// ============================================================
// TYPES
// ============================================================

/** Network context value */
export interface NetworkContextValue {
  // State
  nodes: Node[];
  connections: Connection[];
  selectedNodes: Set<string>;

  // Node Operations
  addNode: (node: Node) => void;
  updateNode: (id: string, updates: Partial<Node>) => void;
  deleteNodes: (ids: string[]) => void;
  getNode: (id: string) => Node | undefined;
  getNodesByType: (type: NodeType) => Node[];

  // Connection Operations
  addConnection: (connection: Connection) => void;
  updateConnection: (id: string, updates: Partial<Connection>) => void;
  deleteConnections: (ids: string[]) => void;
  getConnection: (id: string) => Connection | undefined;
  getNodeConnections: (nodeId: string) => Connection[];

  // Selection Operations
  selectNode: (id: string, addToSelection?: boolean) => void;
  deselectNode: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  deleteSelected: () => void;
  isSelected: (id: string) => boolean;
  selectedNodesList: Node[];

  // Bulk Operations
  clearNetwork: () => void;
  importNetwork: (nodes: Node[], connections: Connection[]) => void;
  exportNetwork: () => { nodes: Node[]; connections: Connection[] };

  // History Operations
  recordHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Computed Stats
  stats: {
    totalNodes: number;
    nodesByType: Record<NodeType, number>;
    totalConnections: number;
    lines: number;
    attachments: number;
    hasBuses: boolean;
    isValidForPowerFlow: boolean;
  };
}

// ============================================================
// CONTEXT
// ============================================================

const NetworkContext = createContext<NetworkContextValue | null>(null);

// ============================================================
// PROVIDER
// ============================================================

export interface NetworkProviderProps {
  children: ReactNode;
  /** Initial nodes */
  initialNodes?: Node[];
  /** Initial connections */
  initialConnections?: Connection[];
  /** Storage key for persistence */
  storageKey?: string;
}

/**
 * NetworkProvider component
 * Wraps children with network state context
 */
export function NetworkProvider({
  children,
  initialNodes = [],
  initialConnections = [],
  storageKey = 'MangoGrid-network',
}: NetworkProviderProps): JSX.Element {
  const networkState = useNetworkState({
    initialNodes,
    initialConnections,
    persist: false, // avoid localStorage persistence in provider to keep tests deterministic
    storageKey,
  });

  // Memoize isSelected helper
  const isSelected = useCallback(
    (id: string) => networkState.selectedNodes.has(id),
    [networkState.selectedNodes]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<NetworkContextValue>(
    () => ({
      // State
      nodes: networkState.nodes,
      connections: networkState.connections,
      selectedNodes: networkState.selectedNodes,

      // Node Operations
      addNode: networkState.addNode,
      updateNode: networkState.updateNode,
      deleteNodes: networkState.deleteNodes,
      getNode: networkState.getNode,
      getNodesByType: networkState.getNodesByType,

      // Connection Operations
      addConnection: networkState.addConnection,
      updateConnection: networkState.updateConnection,
      deleteConnections: networkState.deleteConnections,
      getConnection: networkState.getConnection,
      getNodeConnections: networkState.getNodeConnections,

      // Selection Operations
      selectNode: networkState.selectNode,
      deselectNode: networkState.deselectNode,
      clearSelection: networkState.clearSelection,
      selectAll: networkState.selectAll,
      deleteSelected: networkState.deleteSelected,
      isSelected,
      selectedNodesList: networkState.selectedNodesList,

      // Bulk Operations
      clearNetwork: networkState.clearNetwork,
      importNetwork: (nodes: Node[], connections: Connection[]) => networkState.importNetwork({ nodes, connections }),
      exportNetwork: networkState.exportNetwork,

      // History Operations
      recordHistory: networkState.recordHistory,
      undo: networkState.undo,
      redo: networkState.redo,
      canUndo: networkState.canUndo,
      canRedo: networkState.canRedo,

      // Computed Stats
      stats: networkState.stats,
    }),
    [networkState, isSelected]
  );

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  );
}

// ============================================================
// CONSUMER HOOK
// ============================================================

/**
 * useNetwork hook
 * Access network context from any component
 * 
 * @throws Error if used outside NetworkProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { nodes, addNode, selectedNodes } = useNetwork();
 *   // ...
 * }
 * ```
 */
export function useNetwork(): NetworkContextValue {
  const context = useContext(NetworkContext);

  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }

  return context;
}

// ============================================================
// SELECTIVE HOOKS
// ============================================================

/**
 * useNetworkNodes hook
 * Access only nodes-related state and operations
 */
export function useNetworkNodes() {
  const {
    nodes,
    addNode,
    updateNode,
    deleteNodes,
    getNode,
    getNodesByType,
  } = useNetwork();

  return {
    nodes,
    addNode,
    updateNode,
    deleteNodes,
    getNode,
    getNodesByType,
  };
}

/**
 * useNetworkConnections hook
 * Access only connections-related state and operations
 */
export function useNetworkConnections() {
  const {
    connections,
    addConnection,
    updateConnection,
    deleteConnections,
    getConnection,
    getNodeConnections,
  } = useNetwork();

  return {
    connections,
    addConnection,
    updateConnection,
    deleteConnections,
    getConnection,
    getNodeConnections,
  };
}

/**
 * useNetworkSelection hook
 * Access only selection-related state and operations
 */
export function useNetworkSelection() {
  const {
    selectedNodes,
    selectNode,
    deselectNode,
    clearSelection,
    selectAll,
    deleteSelected,
    isSelected,
    selectedNodesList,
  } = useNetwork();

  return {
    selectedNodes,
    selectNode,
    deselectNode,
    clearSelection,
    selectAll,
    deleteSelected,
    isSelected,
    selectedNodesList,
  };
}

/**
 * useNetworkHistory hook
 * Access only history-related operations
 */
export function useNetworkHistory() {
  const {
    recordHistory,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useNetwork();

  return {
    recordHistory,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}

/**
 * useNetworkStats hook
 * Access only computed statistics
 */
export function useNetworkStats() {
  const { stats } = useNetwork();
  return stats;
}

export default NetworkContext;

